import { useMemo, useState } from 'react';
import { TFilters, TNote } from '../types';
import { filterNotesByCreatedAtDateRange } from '../utils';
import { NoteStatus } from '../enums';
import { Tag } from '@/_models/Tag';

type Args<T extends TNote> = {
  notes: T[];
  searchTerm?: string;
  filters?: TFilters;
  createdAtDateRange?: [Date, Date];
  tags?: Tag[];
};
/**
 * Filters notes to 3 categories:
 *  - `fileredCardNotes` - notes card that shoud be displayed
 *  - `filteredTourNotes` - notes tags that should be visible in the tour
 *  - `hiddenTourNotes` - notes tags that should be hidden in the tour
 */
export function useFilterNotes<T extends TNote>({
  notes,
  filters,
  searchTerm,
  createdAtDateRange,
  tags,
}: Args<T>) {
  const [hiddenTourNotes, setHiddenTourNotes] = useState<T[]>([]);

  // Global filtering (hide/show cards & tour tags)
  const filteredTourNotes = useMemo(() => {
    let filteredNotes = [...notes];

    // Global filtering (hide/show cards & tour tags)
    if (filters?.status && filters.status.length === 1) {
      if (filters.status.includes(NoteStatus.Completed)) {
        filteredNotes = filteredNotes.filter((t) => t.isCompleted);
      } else if (filters.status.includes(NoteStatus.Incompleted)) {
        filteredNotes = filteredNotes.filter((t) => !t.isCompleted);
      }
    }

    if (filters?.tags.length) {
      filteredNotes = filteredNotes.filter((note) => filters.tags.includes(note.tagId));
    }

    setHiddenTourNotes(notes.filter((t) => !filteredNotes.includes(t)));
    return filteredNotes;
  }, [filters, notes]);

  // Local filtering (hide/show cards)
  const filteredCardNotes = useMemo(() => {
    let filteredNotes = [...filteredTourNotes];

    if (searchTerm?.length) {
      filteredNotes = filteredNotes.filter((note) => {
        const noteTag = note.tagId ? tags?.find((tag) => tag.id === note.tagId) : null;

        return (
          note.reporter?.toLowerCase()?.includes(searchTerm) ||
          note.title?.toLowerCase()?.includes(searchTerm) ||
          note.messages.find((message) =>
            message.description?.toLowerCase()?.includes(searchTerm),
          ) ||
          (noteTag
            ? noteTag.title?.toLowerCase()?.includes(searchTerm) ||
              noteTag?.description?.toLowerCase()?.includes(searchTerm)
            : false)
        );
      });
    }

    if (createdAtDateRange) {
      filteredNotes = filterNotesByCreatedAtDateRange(filteredNotes, createdAtDateRange);
    }

    return filteredNotes;
  }, [filteredTourNotes, searchTerm, createdAtDateRange]);

  return { filteredTourNotes, filteredCardNotes, hiddenTourNotes };
}
